// How many Claude calls a reader may spend, and whether this one is allowed.
// The pure half of the translation rate limit: tier limits, the rolling-window
// arithmetic, and the spend decision. The Convex shell (`ai.glossSelection` →
// `consumeTranslation`) supplies the stored counters and persists what this
// returns, so the whole policy is unit-testable without a database.
//
// Only NON-CACHED translations are metered. A cache hit costs nothing and is
// served to anyone, signed in or not (`ai.getCached`), so the limit tracks
// actual Anthropic spend rather than reading activity — a reader who stays in
// well-trodden passages never sees it.
//
// The hourly cap is a TRUE ROLLING WINDOW, kept as the timestamps of the N most
// recent calls (N = the tier's hourly cap), overwritten oldest-first. A fixed
// clock-hour window would be one number per reader instead of five, but it lets
// a reader spend the tail of one hour and the head of the next back to back —
// twice the cap in a couple of minutes — and it can only ever say "come back at
// the top of the hour". Holding the N timestamps costs a tiny bounded array and
// buys both the honest limit and an exact `retryAt`: the instant the oldest
// relevant call ages out.
//
// The DAILY cap (subscribers only) is a plain counter, swept by a nightly cron
// rather than kept as timestamps — 300 timestamps per reader would be a real
// document, and a day boundary has none of the burst problem an hour has.
// `dayStart` is still compared here so a late or missed cron cannot hand a
// reader a second day's allowance; the cron is the sweeper, this is the rule.
//
// BOTH paid tiers share ONE set of limits (`PAID_LIMITS`). The $12 plan's
// differentiator is the book paywall bypass — a separate mechanism entirely
// (`lib/subscriptions.hasAllAccess` → `resolvePremiumAccess`) — not a bigger
// translation allowance, because the Anthropic bill does not care which plan
// bought the call. The one caller that IS exempt from the caps is a founder,
// and that arrives as `opts.unlimited` from the Convex shell rather than as a
// fourth tier, so no reader-facing plan can ever be sold as unmetered.

/** What a reader's plan lets them spend. `all_access` spends at the same rate
 *  as `translations`; what it adds is the paywall bypass, not more calls. */
export type TranslationTier = "free" | "translations" | "all_access";

/** A cap of `null` means unmetered on that axis, not zero. */
export interface TierLimits {
  readonly perHour: number | null;
  readonly perDay: number | null;
}

export const HOUR_MS = 60 * 60 * 1000;
export const DAY_MS = 24 * HOUR_MS;

/** What either paid plan spends: 50 an hour and 300 a day. "Practically
 *  unlimited" for a reader, still bounded against a runaway client. Shared by
 *  BOTH paid tiers through one constant so they cannot drift apart. */
const PAID_LIMITS: TierLimits = { perHour: 50, perDay: 300 };

/**
 * The published limits (one place, so the paywall copy, the meter, and the
 * enforcement can never disagree):
 *   - `free` — 5 an hour. Metered hourly rather than daily so a reader who
 *     runs out is a short wait from reading again instead of locked out until
 *     tomorrow; the subscribe prompt lands as an offer, not a wall. No daily
 *     backstop, so a determined free reader can reach 120 in a day.
 *   - `translations` ($4/mo) — the paid allowance, 50 an hour and 300 a day.
 *   - `all_access` ($12/mo) — the SAME allowance. The extra $8 buys the book
 *     paywall, not more Claude calls.
 */
export const TIER_LIMITS: Record<TranslationTier, TierLimits> = {
  free: { perHour: 5, perDay: null },
  translations: PAID_LIMITS,
  all_access: PAID_LIMITS,
};

/** How the three tiers ORDER — which is a different question from what each
 *  one spends (`TIER_LIMITS`), now that both paid tiers spend the same. The one
 *  ordering: `floorTier` here and `tierAt`'s "best of several live
 *  subscriptions" both read it, so they cannot disagree about which tier wins. */
export const TIER_RANK: Record<TranslationTier, number> = {
  free: 0,
  translations: 1,
  all_access: 2,
};

/**
 * `tier` raised to at least `floor`, never lowered — the rule behind "a staff
 * account reads as all-access without anyone having sold them a plan". A
 * reader already at or above the floor keeps their own tier, so this can only
 * ever be an upgrade.
 */
export function floorTier(
  tier: TranslationTier,
  floor: TranslationTier,
): TranslationTier {
  return TIER_RANK[tier] >= TIER_RANK[floor] ? tier : floor;
}

/**
 * A reader's stored usage.
 *
 * `recent` holds call timestamps in ascending order, capped at the tier's
 * hourly limit — the ring the rolling window reads. `dayCount`/`dayStart` are
 * the nightly-swept daily counter.
 */
export interface TranslationUsage {
  readonly recent: readonly number[];
  readonly dayCount: number;
  readonly dayStart: number;
}

/** How many timestamps a tier needs to keep: exactly its hourly cap, since
 *  older calls can never affect whether the cap is reached. A tier with no
 *  hourly cap keeps none — its spend is a counter, not a queue. */
export function ringCapacity(tier: TranslationTier): number {
  return TIER_LIMITS[tier].perHour ?? 0;
}

/** The start of the fixed window of `size` containing `now`. */
export function windowStart(now: number, size: number): number {
  return Math.floor(now / size) * size;
}

/** The timestamps in `recent` that still fall inside the trailing hour ending
 *  at `now`. Ascending, like the stored array. */
export function withinHour(
  recent: readonly number[],
  now: number,
): readonly number[] {
  const cutoff = now - HOUR_MS;
  return recent.filter((at) => at > cutoff);
}

/**
 * `usage` as it stands at `now`: hour timestamps pruned to the trailing hour,
 * and a daily counter whose day has rolled over read as zero. Absent usage (a
 * reader's first translation ever) is an empty ring in the current day. Never
 * mutates its input.
 */
export function usageAt(
  usage: TranslationUsage | null,
  now: number,
): TranslationUsage {
  const dayStart = windowStart(now, DAY_MS);
  if (usage === null) return { recent: [], dayCount: 0, dayStart };
  return {
    recent: withinHour(usage.recent, now),
    dayCount: usage.dayStart === dayStart ? usage.dayCount : 0,
    dayStart,
  };
}

/** Which cap stopped the call — the client says "a few minutes" or "tomorrow"
 *  from this, and the copy differs. */
export type QuotaScope = "hour" | "day";

export type SpendDecision =
  | { allow: true; next: TranslationUsage; remaining: QuotaRemaining }
  | { allow: false; scope: QuotaScope; retryAt: number; limit: number };

/** What is left on each axis; `null` where the tier is unmetered. */
export interface QuotaRemaining {
  readonly hour: number | null;
  readonly day: number | null;
}

function remainingFor(
  limits: TierLimits,
  usage: TranslationUsage,
): QuotaRemaining {
  return {
    hour:
      limits.perHour === null
        ? null
        : Math.max(0, limits.perHour - usage.recent.length),
    day:
      limits.perDay === null
        ? null
        : Math.max(0, limits.perDay - usage.dayCount),
  };
}

/**
 * What `tier` has left at `now`, without spending anything — the meter the
 * reader sees. Prunes first, so a reader whose calls have aged out reads as
 * full without any write having happened.
 */
export function quotaRemaining(
  tier: TranslationTier,
  usage: TranslationUsage | null,
  now: number,
): QuotaRemaining {
  return remainingFor(TIER_LIMITS[tier], usageAt(usage, now));
}

/**
 * May this reader spend one non-cached translation at `now`?
 *
 * The DAY cap is checked before the hour cap so a reader who has exhausted
 * both is told the truthful longer wait rather than being sent back in minutes
 * to be refused again.
 *
 * `retryAt` on an hourly refusal is exact: the call that has to age out is the
 * `limit`-th most recent one, and a slot opens exactly an hour after it. On a
 * daily refusal it is the next day boundary.
 *
 * On success the caller persists `next` verbatim — pruning, appending, and
 * capping happen here, so storage cannot disagree with the decision about
 * which calls counted.
 *
 * `opts.unlimited` skips BOTH refusals and nothing else: an unlimited caller
 * (a founder, per `lib/subscriptions.effectiveTranslationAccess`) falls
 * through to exactly the success path an under-cap reader takes, so their
 * calls are still counted and still recorded. It is not a tier and cannot be
 * bought — the meter it reports may read zero remaining while still allowing.
 */
export function decideTranslationSpend(
  tier: TranslationTier,
  usage: TranslationUsage | null,
  now: number,
  opts?: { unlimited?: boolean },
): SpendDecision {
  const limits = TIER_LIMITS[tier];
  const current = usageAt(usage, now);
  const unlimited = opts?.unlimited === true;

  if (
    !unlimited &&
    limits.perDay !== null &&
    current.dayCount >= limits.perDay
  ) {
    return {
      allow: false,
      scope: "day",
      retryAt: current.dayStart + DAY_MS,
      limit: limits.perDay,
    };
  }
  if (
    !unlimited &&
    limits.perHour !== null &&
    current.recent.length >= limits.perHour
  ) {
    // Ascending order, so the call that must age out to free a slot is
    // `limit` places from the end — with a full ring that is the oldest, and
    // with an over-full one (a reader whose plan was downgraded under them)
    // it is still the right one.
    const blocking = current.recent[current.recent.length - limits.perHour];
    return {
      allow: false,
      scope: "hour",
      retryAt: (blocking ?? now) + HOUR_MS,
      limit: limits.perHour,
    };
  }

  const capacity = ringCapacity(tier);
  const next: TranslationUsage = {
    // `slice(-0)` is `slice(0)` — the WHOLE array — so a tier with no hourly
    // cap has to be handled before the slice, or its readers would accumulate
    // an unbounded ring of timestamps nothing ever reads.
    recent: capacity === 0 ? [] : [...current.recent, now].slice(-capacity),
    dayCount: current.dayCount + 1,
    dayStart: current.dayStart,
  };
  return { allow: true, next, remaining: remainingFor(limits, next) };
}

/**
 * Reads of a chapter's server copy a signed-in reader may make an hour,
 * spent before each read and before any gloss is charged: six times the
 * paid gloss cap, so no metered reader reaches it; a loop of forged spans
 * reads at most this many chapters (up to about 98 KB each) a clock hour,
 * twice that across an hour boundary.
 */
export const RESOLVE_PER_HOUR = 300;

/** The code a refused read carries; the client words it from `retryAt`. */
export const RESOLVE_LIMIT_ERROR = "rate_limited";

/** A reader's reads so far in the fixed hour that began at `resolveWindowStart`. */
export interface ResolveUsage {
  readonly resolveCount: number;
  readonly resolveWindowStart: number;
}

export type ResolveDecision =
  | { allow: true; next: ResolveUsage }
  | { allow: false; retryAt: number; limit: number };

/**
 * May this reader read a chapter's server copy at `now`? A fixed clock-hour
 * window rather than the rolling ring the gloss cap keeps: two numbers per
 * reader instead of three hundred timestamps, and the burst a fixed window
 * allows across a boundary doubles reads, not Claude calls.
 */
export function decideResolveSpend(
  usage: ResolveUsage | null,
  now: number,
): ResolveDecision {
  const resolveWindowStart = windowStart(now, HOUR_MS);
  const count =
    usage !== null && usage.resolveWindowStart === resolveWindowStart
      ? usage.resolveCount
      : 0;
  if (count >= RESOLVE_PER_HOUR) {
    return {
      allow: false,
      retryAt: resolveWindowStart + HOUR_MS,
      limit: RESOLVE_PER_HOUR,
    };
  }
  return {
    allow: true,
    next: { resolveCount: count + 1, resolveWindowStart },
  };
}

/** Whether the window could still refuse at `now`, so the sweep keeps the row. */
export function resolveWindowOpen(
  usage: ResolveUsage | null,
  now: number,
): boolean {
  return usage !== null && usage.resolveWindowStart + HOUR_MS > now;
}
